<script setup>
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";
    import '@jobinsjp/vue3-datatable/dist/style.css';
    import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
    import moment from 'moment';
    import { truncate } from 'lodash';
    import Swal from 'sweetalert2';
    import axios from 'axios';
    import { computed, onMounted, ref } from 'vue';
    import { useStore } from 'vuex';
    import { authHeader } from './../../../helpers/authservice/auth-header';
    import { required, helpers } from "@vuelidate/validators";
    import useVuelidate from "@vuelidate/core";
    import Multiselect from '@suadelabs/vue3-multiselect';
    import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";


    const store = useStore();
    const loading = computed(() => store.state.globals.loading);
    const whiteLists = computed(() => store.state.whitelistLgu.whiteLists);
    const dropdownMvRegistrationTypes = computed(() => store.state.mvRegistrationType.dropdownMVRegistrationTypes);
    const pagination = computed(() => store.state.whitelistLgu.pagination);
    const notification = computed(() => store.state.notification);
    const lguOptions = computed(() => store ? store.state.lgu.dropdownLGUs : []);
    const title = ref("LGU Whitelist");
    const items = ref([
        {
            text: "Home",
            href: "/"
        },
        {
            text: "Settings",
        },
        {
            text: "LGU Whitelist",
            active: true,
        }
    ]);

    const query = ref({
        search: ''        
    });

    const rules = computed(() => ({
        name: {
            required: helpers.withMessage("Name is required", required),
        },
        motor_vehicle_registration_type_id: {
            required: helpers.withMessage("MV Registration ID is required", required),
        },
        number: {
            required: helpers.withMessage("Plate Number is required", required),
        },
        lgus: {
            required: helpers.withMessage("Please select at least one LGU", required),
        },
    }));

    const submitted = ref(false);

    const whiteList = ref({
        id: '',
        name: '',
        number: '',
        motor_vehicle_registration_type_id: '',
        lgus:null,
        updated_at: null
    })

    const v$ = useVuelidate(rules, whiteList);

    const getAllWhiteLists = async (dt) => {
        await store.dispatch('whitelistLgu/getWhiteLists', {
            page: dt.page,
            pageSize: dt.per_page ,
            sortColumn: 'updated_at',
            search: dt.search,
            orderBy: 'desc'
        });
    }

    const formattedDate = (date) => {
        if (!date) return 'N/A';
        return moment(date).format('YYYY-MM-DD h:mm A')
    }

    const deleteWhiteList = (id, name) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#34c38f",
            cancelButtonColor: "#f46a6a",
            confirmButtonText: "Delete",
        }).then(async (result) => {
            if (result.value) {
                await store.dispatch('whitelistLgu/deleteWhiteList', { id, name });
            }
        });
    }

    const getWhiteListDetails = async (id) => {
        store.dispatch('globals/loading', null, { root: true });

        try {
            const { data } = await axios.get(`/whitelist-local/${id}`, {
                headers: authHeader()
            });
            whiteList.value = data.data;
            store.dispatch('notification/clear', null, { root: true });
            store.dispatch('globals/loaded', null, { root: true });
        } catch (err) {
            console.log(err);
            store.dispatch('globals/loaded', null, { root: true });
        }
    }

    const updateWhiteListsDetails = async () => {
        try {
            submitted.value = true
            v$.value.$touch();
            if (v$.value.$invalid) return;
            await store.dispatch('whitelistLgu/updateWhiteList', whiteList.value)
        } catch (err) {
            console.log(err);
        }
    }

    const getMVRegistrationTypeName = (id) => {
        if (dropdownMvRegistrationTypes.value.length > 0) {
          const mvRegistrationType = dropdownMvRegistrationTypes.value.find((el) => el.id === id);
          return mvRegistrationType.name;
        }
        return 'N/A';
    }

    const getUserLGUs = (values) => {
        if (values.length > 0) {
            let lgus = [];
            values.forEach((value) => {
            lgus.push(value.name);
            })

            return lgus.join(', ');
        }
        return 'N/A';
    }


    onMounted(async () => {
      await store.dispatch('mvRegistrationType/getDropdownMVRegistrationTypes');
      await store.dispatch('lgu/getDropdownLGUs');
    });
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-overlay :show="loading">
        <div class="d-flex mb-4 justify-content-between">
          <div>
            <h1 class="fs-4 text">LGU Whitelist</h1>
            <small>Create and edit whitelisted plate numbers</small>
          </div>
          <div>
             <router-link
                  to="/settings/whitelist-lgu/create"
                  class="btn btn-primary right"
                >
                 <i class="ri-add-fill label-icon align-middle fs-16 me-2"></i>
                  New LGU Whitelist
              </router-link>
          </div>
        </div>

        <div class="row">
            <div class="col lg-12">
                <data-table
                    :rows="whiteLists"
                    :pagination="pagination"
                    striped
                    :loading="loading"
                    :query="query"
                    filter
                    hoverable
                    sortable
                    @loadData="getAllWhiteLists"
                    >

                    <template #thead>
                        <table-head>Name</table-head>
                        <table-head>MV Registration Type</table-head>
                        <table-head>Plate / CS / MV File #</table-head>
                        <table-head>Lgu</table-head>
                        <table-head>Date Modified</table-head>
                        <table-head>Actions</table-head>
                    </template>

                    <template #tbody="{row}">
                        <table-body v-text="truncate(row.name, { length: 30 })" :title="row.name" />
                        <table-body v-text="getMVRegistrationTypeName(row.motor_vehicle_registration_type_id)" />
                        <table-body v-text="truncate(row.number, { length: 30 })" :title="row.number" />
                        <table-body :title="getUserLGUs(row.lgus)">
                            {{ truncate(getUserLGUs(row.lgus), { length: 20 }) }}
                        </table-body>
                        <table-body v-text="formattedDate(row.updated_at)" />
                        <table-body>
                        <button
                            class="btn btn-info mr-3"
                            title="Edit"
                            data-bs-toggle="modal"
                            data-bs-target="#editWhiteListModal"
                            @click="getWhiteListDetails(row.id)"
                        >
                            <i class="ri-pencil-line label-icon align-middle"></i>
                        </button>
                        <button class="btn btn-danger" type="button" title="Delete" @click.prevent="deleteWhiteList(row.id, row.name)">
                            <i class="mdi mdi-delete label-icon align-middle"></i>
                        </button>
                        </table-body>
                    </template>

                    <template #empty>
                    <TableBodyCell colspan="10">
                        No lgu whitelists found.
                    </TableBodyCell>
                </template>
                </data-table>
            </div>
        </div>
      </b-overlay>
  </Layout>
  <!-- Varying modal content -->
  <div class="modal fade" role="dialog" id="editWhiteListModal" tabindex="-1" aria-labelledby="varyingcontentModalLabel" aria-hidden="true">
      <div class="modal-dialog">
          <div class="modal-content">
              <b-overlay :show="loading">
                <div class="modal-header">
                    <h5 class="modal-title" id="varyingcontentModalLabel">LGU Whitelist Details</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                      <form>
                          <b-alert v-if="notification && notification.message"
                              show variant="success" class="fade show" role="alert">
                              {{ notification.message }}
                          </b-alert>

                          <div v-if="notification && notification.messages.length > 0">
                              <b-alert v-for="(message, index) in notification.messages" :key="index"
                                  show variant="danger" class="fade show" role="alert">
                                  {{ message }}
                              </b-alert>
                          </div>
                          <div class="mb-3">
                              <label for="name" class="form-label required">Name</label>
                              <input 
                                :class="{ 'is-invalid': submitted && v$.name.$error }"
                                type="text" class="form-control" id="name" v-model="whiteList.name">
                              <div
                                  v-for="item of v$.name.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="remarks" class="form-label required">MV Registration Type</label>
                              <select 
                                :class="{ 'is-invalid': submitted && v$.motor_vehicle_registration_type_id.$error }"
                                class="form-select" v-model="whiteList.motor_vehicle_registration_type_id">
                                <option v-for="type in dropdownMvRegistrationTypes" :key="type.id" :value="type.id">
                                  {{ type.name }}
                                </option>
                              </select>
                               <div
                                  v-for="item of v$.motor_vehicle_registration_type_id.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="number" class="form-label required">Plate / CS / MV File #</label>
                              <input
                                type="text"
                                id="number"
                                :class="{ 'is-invalid': submitted && v$.number.$error }"
                                class="form-control" v-model="whiteList.number" 
                              />
                              <div
                                  v-for="item of v$.number.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                            <div class="mb-3">
                                <label for="lguInput" class="form-label required">LGU Assign</label>
                                <multiselect
                                    @input="clearNotification"
                                    :class="{ 'is-invalid': submitted && v$.lgus.$error }"
                                    :multiple="true"
                                    v-model="whiteList.lgus"
                                    :close-on-select="true"
                                    :options="lguOptions"
                                    placeholder="Select LGU Assign"
                                    label="name"
                                    track-by="name"
                                />
                                <div
                                    v-for="item of v$.lgus.$errors"
                                    :key="item.$uid"
                                    class="invalid-feedback"
                                >
                                    <span v-if="item.$message">{{ item.$message }}</span>
                                </div>
                            </div>
                      </form>
                       <div class="text-muted">
                          Last Updated: {{ formattedDate(whiteList.updated_at) }}
                        </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-danger" data-bs-dismiss="modal">
                      <i class="mdi mdi-close-thick"></i>
                      Close
                    </button>
                    <button @click.prevent="updateWhiteListsDetails" type="button" class="btn btn-success">
                      <i class="mdi mdi-content-save"></i>
                      Update LGU Whitelist
                    </button>
                </div>
              </b-overlay>
          </div>
      </div>
    </div>
</template>
